import { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import Select from 'react-select';
import moment from 'moment';
import { tConvert, toLocalDateString, getUserData } from '../../helpers';
import { schedulesService } from '../../services';

// Request type options
const REQUEST_TYPE_OPTIONS = [
  { label: 'Day-Off (Change of Rest Day)', value: 'dayOff' },
  { label: 'Shift (Change of Schedule – Same Day)', value: 'shift' },
  {
    label: 'Shift Swap (Schedule Swap with Team Member)',
    value: 'shiftSwap',
  },
];

// react-select theme (consistent with other scheduling components)
const selectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: '#0649b5',
    neutral10: '#edf2f9',
    neutral20: '#d2ddec',
    neutral30: '#d2ddec',
    neutral50: '#b1c2da',
    neutral80: '#12263f',
    dangerLight: '#DEEBFF',
    danger: '#12263f',
  },
});

const datePickerOptions = {
  allowInvalidPreload: true,
  dateFormat: 'M j, Y',
  monthSelectorType: 'static',
};

const TYPE_MAP = {
  'shift-change': 'shift',
  'shift-swap': 'shiftSwap',
  'day-off': 'dayOff',
};

// Build validation schema based on request type
const getValidationSchema = (requestType) => {
  const base = {
    requestType: Yup.string().required('Please select a request type'),
    reason: Yup.string().trim().required('Please provide a reason'),
  };

  if (requestType === 'dayOff') {
    return Yup.object({
      ...base,
      dayOff: Yup.date().required('Please select the day-off date').nullable(),
      dayOffShiftId: Yup.string().required(
        'Please select the shift for the day-off'
      ),
      workDay: Yup.date().required('Please select the work day').nullable(),
      workDayShiftId: Yup.string().required(
        'Please select a shift for the work day'
      ),
    });
  }

  if (requestType === 'shift') {
    return Yup.object({
      ...base,
      date: Yup.date().required('Please select a date').nullable(),
      originalShiftId: Yup.string().required(
        'Please select the original shift'
      ),
      newShiftId: Yup.string().required('Please select the new shift'),
    });
  }

  if (requestType === 'shiftSwap') {
    return Yup.object({
      ...base,
      date: Yup.date().required('Please select a date').nullable(),
      originalShiftId: Yup.string().required(
        'Please select the original shift'
      ),
      swapWithMemberId: Yup.string().required(
        'Please select a team member to swap with'
      ),
      swapScheduleId: Yup.string().required(
        "Please wait for the member's schedule to load"
      ),
    });
  }

  // Default — just request type + reason
  return Yup.object(base);
};

const CreateChangeRequest = ({
  showCreateRequest,
  handleCreateRequestDisplay,
  handleCreateRequest,
  handleEditRequest,
  editingRequest,
  exitingShifts,
  swapMembersList,
}) => {
  const isEditMode = !!editingRequest;
  const [requestType, setRequestType] = useState(
    isEditMode ? TYPE_MAP[editingRequest.type] || '' : ''
  );
  const [scheduleOnDate, setScheduleOnDate] = useState([]);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [dayOffSchedule, setDayOffSchedule] = useState([]);
  const [isLoadingDayOffSchedule, setIsLoadingDayOffSchedule] = useState(false);
  const [swapMemberSchedule, setSwapMemberSchedule] = useState([]);
  const [isLoadingSwapSchedule, setIsLoadingSwapSchedule] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchScheduleForDate = (date, setFieldValue) => {
    setIsLoadingSchedule(true);
    setScheduleOnDate([]);
    setFieldValue('originalShiftId', '');
    setFieldValue('originalShiftLabel', '');
    setFieldValue('originalFrom', '');
    setFieldValue('originalTo', '');
    schedulesService
      .get_shift_change_details({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        userId: getUserData('_id'),
        date: moment(date).format('YYYY-MM-DD'),
      })
      .then((response) => {
        if (response.data?.length > 0) {
          setScheduleOnDate(response.data);
          if (response.data.length === 1) {
            const [schedule] = response.data;
            setFieldValue('originalShiftId', schedule.shiftId);
            setFieldValue('originalScheduleId', schedule.scheduleId);
            setFieldValue('originalShiftLabel', schedule.name);
            setFieldValue('originalFrom', schedule.shiftStart);
            setFieldValue('originalTo', schedule.shiftEnd);
          }
        }
      })
      .finally(() => {
        setIsLoadingSchedule(false);
      });
  };

  const fetchDayOffSchedule = (date, setFieldValue) => {
    setIsLoadingDayOffSchedule(true);
    setDayOffSchedule([]);
    setFieldValue('dayOffShiftId', '');
    setFieldValue('dayOffScheduleId', '');
    schedulesService
      .get_shift_change_details({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        userId: getUserData('_id'),
        date: moment(date).format('YYYY-MM-DD'),
      })
      .then((response) => {
        if (response.data?.length > 0) {
          setDayOffSchedule(response.data);
          if (response.data.length === 1) {
            setFieldValue('dayOffShiftId', response.data[0].shiftId);
            setFieldValue('dayOffScheduleId', response.data[0].scheduleId);
          }
        }
      })
      .finally(() => {
        setIsLoadingDayOffSchedule(false);
      });
  };

  const fetchSwapMemberSchedule = (partnerId, date, setFieldValue) => {
    setIsLoadingSwapSchedule(true);
    setSwapMemberSchedule([]);
    setFieldValue('newShiftId', '');
    setFieldValue('swapScheduleId', '');
    schedulesService
      .get_shift_change_details({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        userId: partnerId,
        date: moment(date).format('YYYY-MM-DD'),
      })
      .then((response) => {
        if (response.data?.length > 0) {
          setSwapMemberSchedule(response.data);
          if (response.data.length === 1) {
            const [schedule] = response.data;
            setFieldValue('newShiftId', schedule.shiftId);
            setFieldValue('swapScheduleId', schedule.scheduleId);
          }
        }
      })
      .finally(() => {
        setIsLoadingSwapSchedule(false);
      });
  };

  // In edit mode, pre-fetch the schedule for the pre-filled date
  useEffect(() => {
    if (!isEditMode) return;
    const mappedType = TYPE_MAP[editingRequest.type];
    if (
      (mappedType === 'shift' || mappedType === 'shiftSwap') &&
      editingRequest.originalSchedule?.shiftStart
    ) {
      fetchScheduleForDate(
        new Date(editingRequest.originalSchedule.shiftStart),
        () => {}
      );
    }
    if (
      mappedType === 'dayOff' &&
      editingRequest.originalSchedule?.shiftStart
    ) {
      fetchDayOffSchedule(
        new Date(editingRequest.originalSchedule.shiftStart),
        () => {}
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Map API shift data to react-select options
  const shiftOptions = (exitingShifts || []).map((shift) => ({
    value: shift.shiftId,
    label: shift.name,
    shiftStart: shift.shiftStart,
    shiftEnd: shift.shiftEnd,
  }));
  const editType = isEditMode ? TYPE_MAP[editingRequest.type] || '' : '';
  const editDate =
    isEditMode && editingRequest.originalSchedule?.shiftStart
      ? toLocalDateString(new Date(editingRequest.originalSchedule.shiftStart))
      : null;

  const initialValues = {
    requestType: editType || '',
    dayOff: editType === 'dayOff' ? editDate : null,
    dayOffShiftId: '',
    dayOffScheduleId: '',
    workDay: null,
    workDayShiftId: '',
    workDayShiftLabel: '',
    workDayShiftFrom: '',
    workDayShiftTo: '',
    date: editType === 'shift' || editType === 'shiftSwap' ? editDate : null,
    originalShiftId: '',
    originalScheduleId: '',
    originalShiftLabel: '',
    originalFrom: '',
    originalTo: '',
    newShiftId: '',
    newShiftLabel: '',
    newFrom: '',
    newTo: '',
    swapWithMemberId: '',
    swapWithLabel: '',
    swapScheduleId: '',
    reason: isEditMode ? editingRequest.requestReason || '' : '',
  };

  const scheduleOptions = scheduleOnDate.map((s) => ({
    value: s.shiftId,
    label: s.name,
    shiftStart: s.shiftStart,
    shiftEnd: s.shiftEnd,
    scheduleId: s.scheduleId,
  }));

  const swapScheduleOptions = swapMemberSchedule.map((s) => ({
    value: s.scheduleId,
    label: s.name,
    shiftStart: s.shiftStart,
    shiftEnd: s.shiftEnd,
    shiftId: s.shiftId,
  }));

  const memberOptions = (swapMembersList || []).map((m) => ({
    value: m.userId,
    label: `${m.firstName} ${m.lastName || ''}`.trim(),
    employeeId: m.employeeId,
  }));

  return (
    <Modal
      show={showCreateRequest}
      onHide={() => handleCreateRequestDisplay(false)}
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title className="m-0">
          {isEditMode ? 'Edit Change Request' : 'New Change Request'}
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => handleCreateRequestDisplay(false)}
        />
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(requestType)}
        onSubmit={(values) => {
          setIsSubmitting(true);
          const handler = isEditMode ? handleEditRequest : handleCreateRequest;
          handler(values, () => setIsSubmitting(false));
        }}
      >
        {(formikprops) => (
          <Form onSubmit={formikprops.handleSubmit}>
            <Modal.Body>
              {/* Request Type */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Request Type <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  options={REQUEST_TYPE_OPTIONS}
                  className="basic-multi-select"
                  placeholder="Select request type"
                  name="requestType"
                  isDisabled={isEditMode}
                  value={
                    REQUEST_TYPE_OPTIONS.find(
                      (opt) => opt.value === formikprops.values.requestType
                    ) || null
                  }
                  onChange={(event) => {
                    const newType = event.value;
                    setRequestType(newType);
                    setScheduleOnDate([]);
                    setDayOffSchedule([]);
                    setSwapMemberSchedule([]);
                    formikprops.setFieldValue('requestType', newType);
                    // Reset type-specific fields on type change
                    formikprops.setFieldValue('dayOff', null);
                    formikprops.setFieldValue('dayOffShiftId', '');
                    formikprops.setFieldValue('dayOffScheduleId', '');
                    formikprops.setFieldValue('workDay', null);
                    formikprops.setFieldValue('workDayShiftId', '');
                    formikprops.setFieldValue('workDayShiftLabel', '');
                    formikprops.setFieldValue('workDayShiftFrom', '');
                    formikprops.setFieldValue('workDayShiftTo', '');
                    formikprops.setFieldValue('date', null);
                    formikprops.setFieldValue('originalShiftId', '');
                    formikprops.setFieldValue('originalShiftLabel', '');
                    formikprops.setFieldValue('originalFrom', '');
                    formikprops.setFieldValue('originalTo', '');
                    formikprops.setFieldValue('newShiftId', '');
                    formikprops.setFieldValue('newShiftLabel', '');
                    formikprops.setFieldValue('newFrom', '');
                    formikprops.setFieldValue('newTo', '');
                    formikprops.setFieldValue('swapWithMemberId', '');
                    formikprops.setFieldValue('swapWithLabel', '');
                    formikprops.setFieldValue('swapScheduleId', '');
                  }}
                  theme={selectTheme}
                />
                <ErrorMessage
                  name="requestType"
                  render={(msg) => <small className="text-danger">{msg}</small>}
                />
              </Form.Group>

              {/* ============ Day-Off Fields ============ */}
              {requestType === 'dayOff' && (
                <>
                  {/* Day-Off date */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Day-Off Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Flatpickr
                      value={
                        formikprops.values.dayOff
                          ? new Date(formikprops.values.dayOff)
                          : undefined
                      }
                      onChange={([date]) => {
                        formikprops.setFieldValue(
                          'dayOff',
                          toLocalDateString(date)
                        );
                        fetchDayOffSchedule(date, formikprops.setFieldValue);
                      }}
                      placeholder="Select day-off date"
                      options={datePickerOptions}
                      className="form-control icon-calendar"
                      name="dayOff"
                    />
                    <ErrorMessage
                      name="dayOff"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                  </Form.Group>

                  {/* Day-Off shift — shown after date is picked */}
                  {formikprops.values.dayOff && (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Day-Off Shift <span className="text-danger">*</span>
                      </Form.Label>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isLoadingDayOffSchedule ? (
                        <p className="text-muted small mb-0">
                          Looking up your schedule…
                        </p>
                      ) : dayOffSchedule.length === 0 ? (
                        <p className="text-warning small mb-0">
                          No schedule found for the selected day-off date.
                        </p>
                      ) : (
                        <>
                          <Select
                            options={dayOffSchedule.map((s) => ({
                              value: s.shiftId,
                              label: s.name,
                              shiftStart: s.shiftStart,
                              shiftEnd: s.shiftEnd,
                              scheduleId: s.scheduleId,
                            }))}
                            className="basic-multi-select"
                            placeholder="Select your shift on that day"
                            name="dayOffShiftId"
                            value={
                              dayOffSchedule
                                .map((s) => ({
                                  value: s.shiftId,
                                  label: s.name,
                                  shiftStart: s.shiftStart,
                                  shiftEnd: s.shiftEnd,
                                  scheduleId: s.scheduleId,
                                }))
                                .find(
                                  (o) =>
                                    o.value === formikprops.values.dayOffShiftId
                                ) || null
                            }
                            onChange={(event) => {
                              formikprops.setFieldValue(
                                'dayOffShiftId',
                                event.value
                              );
                              formikprops.setFieldValue(
                                'dayOffScheduleId',
                                event.scheduleId
                              );
                            }}
                            theme={selectTheme}
                            formatOptionLabel={(option) => (
                              <div>
                                {option.label}{' '}
                                <small className="text-muted">
                                  ({tConvert(option.shiftStart)} –{' '}
                                  {tConvert(option.shiftEnd)})
                                </small>
                              </div>
                            )}
                          />
                          <ErrorMessage
                            name="dayOffShiftId"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                        </>
                      )}
                    </Form.Group>
                  )}

                  {/* Work Day date */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Work Day <span className="text-danger">*</span>
                    </Form.Label>
                    <Flatpickr
                      value={
                        formikprops.values.workDay
                          ? new Date(formikprops.values.workDay)
                          : undefined
                      }
                      onChange={([date]) => {
                        formikprops.setFieldValue(
                          'workDay',
                          toLocalDateString(date)
                        );
                      }}
                      placeholder="Select work day"
                      options={datePickerOptions}
                      className="form-control icon-calendar"
                      name="workDay"
                    />
                    <ErrorMessage
                      name="workDay"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                  </Form.Group>

                  {/* Work Day shift */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Work Day Schedule <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      options={shiftOptions}
                      className="basic-multi-select"
                      placeholder="Select shift for work day"
                      name="workDayShiftId"
                      onChange={(event) => {
                        formikprops.setFieldValue(
                          'workDayShiftId',
                          event.value
                        );
                        formikprops.setFieldValue(
                          'workDayShiftLabel',
                          event.label
                        );
                        formikprops.setFieldValue(
                          'workDayShiftFrom',
                          event.shiftStart
                        );
                        formikprops.setFieldValue(
                          'workDayShiftTo',
                          event.shiftEnd
                        );
                      }}
                      theme={selectTheme}
                      formatOptionLabel={(option) => (
                        <div>
                          {option.label}{' '}
                          <small className="text-muted">
                            ({tConvert(option.shiftStart)} –{' '}
                            {tConvert(option.shiftEnd)})
                          </small>
                        </div>
                      )}
                    />
                    <ErrorMessage
                      name="workDayShiftId"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                    {formikprops.values.workDayShiftFrom && (
                      <small className="text-muted mt-1 d-block">
                        Shift time:{' '}
                        {tConvert(formikprops.values.workDayShiftFrom)} –{' '}
                        {tConvert(formikprops.values.workDayShiftTo)}
                      </small>
                    )}
                  </Form.Group>
                </>
              )}

              {/* ============ Shift / Shift Swap Fields ============ */}
              {(requestType === 'shift' || requestType === 'shiftSwap') && (
                <>
                  {/* Single date field */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Flatpickr
                      value={
                        formikprops.values.date
                          ? new Date(formikprops.values.date)
                          : undefined
                      }
                      onChange={([date]) => {
                        formikprops.setFieldValue(
                          'date',
                          toLocalDateString(date)
                        );
                        setSwapMemberSchedule([]);
                        formikprops.setFieldValue('swapWithMemberId', '');
                        formikprops.setFieldValue('swapWithLabel', '');
                        formikprops.setFieldValue('swapScheduleId', '');
                        fetchScheduleForDate(date, formikprops.setFieldValue);
                      }}
                      placeholder="Select date"
                      options={datePickerOptions}
                      className="form-control icon-calendar"
                      name="date"
                    />
                    <ErrorMessage
                      name="date"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                  </Form.Group>

                  {/* Original Schedule — shown after date is selected */}
                  {formikprops.values.date && (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Original Schedule <span className="text-danger">*</span>
                      </Form.Label>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isLoadingSchedule ? (
                        <p className="text-muted small mb-0">
                          Looking up your schedule…
                        </p>
                      ) : scheduleOptions.length === 0 &&
                        formikprops.values.date ? (
                        <p className="text-warning small mb-0">
                          No schedule found for the selected date.
                        </p>
                      ) : (
                        <>
                          <Select
                            options={scheduleOptions}
                            className="basic-multi-select"
                            placeholder="Select your current shift"
                            name="originalShiftId"
                            value={
                              scheduleOptions.find(
                                (o) =>
                                  o.value === formikprops.values.originalShiftId
                              ) || null
                            }
                            onChange={(event) => {
                              formikprops.setFieldValue(
                                'originalShiftId',
                                event.value
                              );
                              formikprops.setFieldValue(
                                'originalScheduleId',
                                event.scheduleId
                              );
                              formikprops.setFieldValue(
                                'originalShiftLabel',
                                event.label
                              );
                              formikprops.setFieldValue(
                                'originalFrom',
                                event.shiftStart
                              );
                              formikprops.setFieldValue(
                                'originalTo',
                                event.shiftEnd
                              );
                            }}
                            theme={selectTheme}
                            formatOptionLabel={(option) => (
                              <div>
                                {option.label}{' '}
                                <small className="text-muted">
                                  ({tConvert(option.shiftStart)} –{' '}
                                  {tConvert(option.shiftEnd)})
                                </small>
                              </div>
                            )}
                          />
                          <ErrorMessage
                            name="originalShiftId"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                          {formikprops.values.originalFrom && (
                            <small className="text-muted mt-1 d-block">
                              Shift time:{' '}
                              {tConvert(formikprops.values.originalFrom)} –{' '}
                              {tConvert(formikprops.values.originalTo)}
                            </small>
                          )}
                        </>
                      )}
                    </Form.Group>
                  )}

                  {/* New Schedule — shift type only */}
                  {requestType === 'shift' && (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        New Schedule <span className="text-danger">*</span>
                      </Form.Label>
                      <Select
                        options={shiftOptions}
                        className="basic-multi-select"
                        placeholder="Select new shift"
                        name="newShiftId"
                        value={
                          shiftOptions.find(
                            (o) => o.value === formikprops.values.newShiftId
                          ) || null
                        }
                        onChange={(event) => {
                          formikprops.setFieldValue('newShiftId', event.value);
                          formikprops.setFieldValue(
                            'newShiftLabel',
                            event.label
                          );
                          formikprops.setFieldValue(
                            'newFrom',
                            event.shiftStart
                          );
                          formikprops.setFieldValue('newTo', event.shiftEnd);
                        }}
                        theme={selectTheme}
                        formatOptionLabel={(option) => (
                          <div>
                            {option.label}{' '}
                            <small className="text-muted">
                              ({tConvert(option.shiftStart)} –{' '}
                              {tConvert(option.shiftEnd)})
                            </small>
                          </div>
                        )}
                      />
                      <ErrorMessage
                        name="newShiftId"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                      {formikprops.values.newFrom && (
                        <small className="text-muted mt-1 d-block">
                          Shift time: {tConvert(formikprops.values.newFrom)} –{' '}
                          {tConvert(formikprops.values.newTo)}
                        </small>
                      )}
                    </Form.Group>
                  )}

                  {/* Swap With + Swap Schedule — shiftSwap only */}
                  {requestType === 'shiftSwap' && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Swap With <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={memberOptions}
                          className="basic-multi-select"
                          placeholder="Select team member"
                          name="swapWithMemberId"
                          value={
                            memberOptions.find(
                              (o) =>
                                o.value === formikprops.values.swapWithMemberId
                            ) || null
                          }
                          onChange={(event) => {
                            formikprops.setFieldValue(
                              'swapWithMemberId',
                              event.value
                            );
                            formikprops.setFieldValue(
                              'swapWithLabel',
                              event.label
                            );
                            if (formikprops.values.date) {
                              fetchSwapMemberSchedule(
                                event.value,
                                formikprops.values.date,
                                formikprops.setFieldValue
                              );
                            }
                          }}
                          theme={selectTheme}
                          formatOptionLabel={(option) => (
                            <div>
                              {option.label}{' '}
                              {option.employeeId && (
                                <small className="text-muted">
                                  ({option.employeeId})
                                </small>
                              )}
                            </div>
                          )}
                        />
                        <ErrorMessage
                          name="swapWithMemberId"
                          render={(msg) => (
                            <small className="text-danger">{msg}</small>
                          )}
                        />
                      </Form.Group>

                      {formikprops.values.swapWithMemberId && (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Their Schedule{' '}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          {/* eslint-disable-next-line no-nested-ternary */}
                          {isLoadingSwapSchedule ? (
                            <p className="text-muted small mb-0">
                              Looking up their schedule…
                            </p>
                          ) : swapScheduleOptions.length === 0 ? (
                            <p className="text-warning small mb-0">
                              No schedule found for this member on the selected
                              date.
                            </p>
                          ) : (
                            <>
                              <Select
                                options={swapScheduleOptions}
                                className="basic-multi-select"
                                placeholder="Select their shift"
                                name="swapScheduleId"
                                value={
                                  swapScheduleOptions.find(
                                    (o) =>
                                      o.value ===
                                      formikprops.values.swapScheduleId
                                  ) || null
                                }
                                onChange={(event) => {
                                  formikprops.setFieldValue(
                                    'swapScheduleId',
                                    event.value
                                  );
                                  formikprops.setFieldValue(
                                    'newShiftId',
                                    event.shiftId
                                  );
                                }}
                                theme={selectTheme}
                                formatOptionLabel={(option) => (
                                  <div>
                                    {option.label}{' '}
                                    <small className="text-muted">
                                      ({tConvert(option.shiftStart)} –{' '}
                                      {tConvert(option.shiftEnd)})
                                    </small>
                                  </div>
                                )}
                              />
                              <ErrorMessage
                                name="swapScheduleId"
                                render={(msg) => (
                                  <small className="text-danger">{msg}</small>
                                )}
                              />
                            </>
                          )}
                        </Form.Group>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ============ Reason (Common) ============ */}
              {requestType && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    Reason <span className="text-danger">*</span>
                  </Form.Label>
                  <Field
                    as="textarea"
                    className="form-control"
                    placeholder="Provide a reason for this request"
                    name="reason"
                    rows={3}
                  />
                  <ErrorMessage
                    name="reason"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </Form.Group>
              )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button
                variant="link"
                onClick={() => handleCreateRequestDisplay(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!requestType || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-1"
                    />
                    {isEditMode ? 'Updating...' : 'Submitting...'}
                  </>
                ) : null}
                {!isSubmitting &&
                  (isEditMode ? 'Update Request' : 'Submit Request')}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

CreateChangeRequest.propTypes = {
  showCreateRequest: PropTypes.bool,
  handleCreateRequestDisplay: PropTypes.func,
  handleCreateRequest: PropTypes.func,
  handleEditRequest: PropTypes.func,
  editingRequest: PropTypes.object,
  exitingShifts: PropTypes.array,
  swapMembersList: PropTypes.array,
};

export default CreateChangeRequest;
